import { useHalFormValues } from 'domain/hooks';
import { useHal } from 'domain/hooks/hal/hal.service';
import { useSchemas } from 'domain/hooks/schemas/schema.service';
import { useAlertState } from 'layout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useMemo } from 'react';
import { JsonRenderer } from './JsonRenderer';
import { Alert, Button, Row, Title } from 'components';
import { useTranslation } from 'react-i18next';
import { AlertPreview } from './AlertPreview';
import { VirtualStationPreview } from './VirtualStationPreview';
import { RoleGuard } from 'components/RoleGuard';
import { cn } from 'domain/core';
import { DerivedSide } from 'pages/derived/DerivedSide';
import { UserOptions } from './UserOptions';

const useSideOptions = (schemaId: string | undefined) => {
	if (!schemaId) {
		return {
			sideEnable: false,
		};
	}

	return {
		sideEnable: true,
	};
};

export const SchemaEdit = () => {
	const { id: schemaId } = useParams<{ id: string | undefined }>();

	const { sideEnable } = useSideOptions(schemaId);

	const navigate = useNavigate();
	const { pushAlert } = useAlertState();

	// Get the query params from the url.
	const { search } = useLocation();

	// Get the id, if not, this is a creation page.
	const id = useMemo(() => {
		const params = new URLSearchParams(search);
		return params.get('id');
	}, [search]);

	const isEdit = useMemo(() => {
		return !!id;
	}, [id]);

	const { schema, uiSchema } = useSchemas(schemaId);

	const halService = useHal(schemaId as string);
	const [values, , setValues] = useHalFormValues({ isEdit, halService, id });

	const { t } = useTranslation(['labels', 'messages']);

	const deleteOnClick = async (e: any) => {
		e.preventDefault();

		if (isEdit) {
			try {
				await halService.remove(values);
				pushAlert({
					alert: Alert.success({
						title: t('labels:notification.success_title'),
						message: t('labels:notification.entry_success'),
						symbol: Symbol('success'),
					}),
					duration: 3000,
				});
				navigate(-1);
			} catch (err) {
				pushAlert({
					alert: Alert.error({
						title: t('labels:notification.error_title'),
						message: t('labels:notification.entry_error'),
						symbol: Symbol('error'),
					}),
					duration: 3000,
				});
			}
		} else {
			// Location to the list.
			navigate(-1);
		}
	};

	const saveOnClick = async (e: any) => {
		e.preventDefault();
		try {
			if (isEdit) {
				await halService.update(values);
			} else {
				await halService.create(values);
			}
			pushAlert({
				alert: Alert.success({
					title: t('labels:notification.success_title'),
					message: t('labels:notification.entry_success'),
					symbol: Symbol('success'),
				}),
				duration: 3000,
			});
			navigate(-1);
		} catch (err) {
			console.log('Error', err);
			pushAlert({
				alert: Alert.error({
					title: t('labels:notification.error_title'),
					message: t('labels:notification.entry_error'),
					symbol: Symbol('error'),
				}),
				duration: 3000,
			});
		}
	};

	const label = useMemo(() => {
		if (values) {
			if (values.name) {
				return values.name;
			}
			if (values.label) {
				return values.label;
			}
		}
		return id;
	}, [id, values]);

	return (
		<div className='w-full md:mt-16'>
			<div className='flex flex-col items-center justify-between bg-white px-2 py-6 md:mx-16 md:px-12'>
				<div className='flex w-full flex-row items-center justify-between'>
					<RoleGuard role='ROLE_USER'>
						<Title>
							{t(`labels:view`)} {label}
						</Title>
					</RoleGuard>
					<RoleGuard role='ROLE_ADMIN'>
						<span className='flex w-full text-xl'>
							{t(`labels:${isEdit ? 'edit' : 'create'}`)} {label ?? ''}
						</span>
						<Row className={cn('justify-end', 'gap-4')}>
							{isEdit && (
								<Button
									onClick={deleteOnClick}
									size='sm'
									className='bg-red-500 hover:bg-red-700'>
									{t(`labels:delete`)}
								</Button>
							)}
							<Button onClick={saveOnClick} size='sm'>
								{t(`labels:save`)}
							</Button>
						</Row>
					</RoleGuard>
				</div>
				<div
					className={cn(
						'mt-2',
						'flex',
						'w-full',
						'flex-row',
						'flex-wrap',
						'items-start',
						'items-stretch',
						'justify-between gap-4'
					)}>
					<div className='min-w-96 flex w-96 flex-col gap-0'>
						{schemaId && (
							<JsonRenderer
								schemaId={schemaId as string}
								schema={schema}
								uiSchema={uiSchema}
								values={values}
								onChange={setValues}
							/>
						)}
					</div>
					{sideEnable && (
						<div className='ml-4 min-h-full min-w-[300px] flex-1 '>
							<SideEditRenderer
								values={values}
								schemaId={schemaId}
								update={setValues}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const SideEditRenderer = ({
	values,
	schemaId,
	update,
}: {
	values: any;
	schemaId: string | undefined;
	update?: (values: any) => void;
}) => {
	if (!schemaId) {
		return null;
	}

	if (schemaId === 'alert-definitions') {
		// Preview the alert.
		return <AlertPreview values={values} />;
	}

	if (schemaId === 'derived') {
		return <DerivedSide values={values} />;
	}

	if (schemaId === 'users') {
		return <UserOptions values={values} />;
	}

	if (schemaId === 'virtual-stations') {
		// Preview the virtual station.
		return <VirtualStationPreview values={values} />;
	}

	return null;
};
